import { AsYouType } from 'libphonenumber-js';
import { TFunction } from 'react-i18next';

import { parseValue } from 'common/dist/utils/types';
import * as api from '../api';
import * as logger from '../logger';
import * as store from '../reducers/store';

import * as FormUtils from './form';

export const getMaybeSelectedEhrProviderWithPractice = (
  ehrProviderInformation: api.User.EhrProviderInformationPractice[] | null,
  selectedTimeSlot: store.SelectedEhrTimeSlot | null,
  selectedProviderId: string | null,
): api.User.EhrProviderInformationPractice | null => {
  if (!ehrProviderInformation) return null;
  if (selectedTimeSlot) {
    return (
      ehrProviderInformation.find(
        (ehrProvider) =>
          ehrProvider.practiceId === selectedTimeSlot.practiceId &&
          (!selectedTimeSlot.providerId ||
            (selectedTimeSlot.providerId &&
              ehrProvider.providerUserId === selectedTimeSlot.providerId)),
      ) || null
    );
  }
  if (!selectedProviderId) return null;
  return (
    ehrProviderInformation.find(
      (ehrProvider) => ehrProvider.providerUserId === selectedProviderId,
    ) || null
  );
};

export const maybeBookSelectedEhrTimeSlots = async (
  previsitFormSetId: string,
  fields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
  setFormField?: (formFieldId: string, value: string) => void,
): Promise<api.EhrTimeSlot.TimeSlotConfirmation[]> => {
  const visibleAptWithValue = fields.filter(
    (f) =>
      f.primitive ===
        api.PrevisitFormTemplateFieldConfig.Primitive.ScheduleAppointment &&
      f.value &&
      !FormUtils.checkIsHidden(f, fields, null),
  );
  if (!visibleAptWithValue) return [];
  const bookedApt = await Promise.all(
    visibleAptWithValue.map((fieldApt) =>
      bookFormFieldEhrTimeSlot(previsitFormSetId, fieldApt, setFormField),
    ),
  );
  return bookedApt.filter(
    (a): a is api.EhrTimeSlot.TimeSlotConfirmation => !!a,
  );
};

export const getMaybeFormSetSelectedTimeSlot = (
  fields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
  page?: number,
  patient?: api.Patient.Patient,
): store.SelectedEhrTimeSlot | null => {
  const parsedFormField = FormUtils.getFormFieldAndParsedValue<{
    selectedAppointment: api.EhrTimeSlot.EhrTimeSlot;
  }>(
    fields,
    api.PrevisitFormTemplateFieldConfig.Primitive.ScheduleAppointment,
    page,
    patient,
  );
  if (parsedFormField?.value) {
    const { selectedAppointment } = parsedFormField?.value ?? {};
    return {
      ...selectedAppointment,
      previsitFormTemplateFieldConfigId:
        parsedFormField.formField.previsitFormTemplateFieldConfigId,
      scheduleAppointmentDisplayBy:
        parsedFormField.formField.scheduleAppointmentDisplayBy,
    };
  }
  return null;
};

export const getRegistrationErrorMessage = (
  t: TFunction,
  phoneToCall?: string,
): string => {
  return `${t(
    'Sorry, that time could not be booked. Please try selecting a different day',
  )}${
    phoneToCall
      ? t(', or call {{phoneToCall}} for assistance.', {
          phoneToCall: new AsYouType('US').input(phoneToCall),
        })
      : '.'
  }`;
};

///////////////////////////////////////////////////
////// PRIVATE

const bookFormFieldEhrTimeSlot = async (
  previsitFormSetId: string,
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig,
  setFormField?: (formFieldId: string, value: string) => void,
): Promise<api.EhrTimeSlot.TimeSlotConfirmation | undefined> => {
  const logPrefix = '[utils][scheduling][maybeBookSelectedEhrTimeSlots]';
  if (
    field.primitive !==
    api.PrevisitFormTemplateFieldConfig.Primitive.ScheduleAppointment
  ) {
    logger.error(
      new Error(`${logPrefix} Trying to book ehr time slot with invalid field`),
    );
    return;
  }
  const { selectedAppointment } =
    parseValue<{
      selectedAppointment: api.EhrTimeSlot.EhrTimeSlot &
        Partial<api.EhrTimeSlot.TimeSlotConfirmation>;
    }>(field.value) ?? {};

  if (!selectedAppointment) {
    logger.error(
      new Error(
        `${logPrefix} Trying to book ehr time slot with invalid field value`,
      ),
    );
    return;
  }
  if (
    selectedAppointment.providerFirstName ||
    selectedAppointment.providerLastName ||
    selectedAppointment.practiceName
  ) {
    console.info('Appointment is already booked, moving on.');
    return;
  }
  let bookedSlot: api.EhrTimeSlot.TimeSlotConfirmation;
  try {
    bookedSlot = await api.client.EhrTimeSlot.bookTimeSlot({
      previsitFormFieldId: field.id,
      ehrTimeSlotId: selectedAppointment?.id,
      ...selectedAppointment,
    });
  } catch (err) {
    logger.error(err, `${logPrefix} Unable to book appointment`);
    throw err;
  }
  try {
    const value = JSON.stringify({
      selectedAppointment: { ...selectedAppointment, ...bookedSlot },
    });
    setFormField && setFormField(field.id, value);
    await api.client.Previsit.updateFormField(
      previsitFormSetId,
      field.id,
      value,
    );
  } catch (err) {
    logger.error(err, `${logPrefix} Unable to update form field`);
  }
  return bookedSlot;
};
